import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import instance from "../../../api/instance";
import endpoints from "../../../api/endpoints";
import parseNumber from "../../../utils/parse-number";
import { RootState } from "../../../redux/store";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

type FilterType = "all" | "user";

interface ProfitLossRow {
  user_name?: string;
  level?: string;
  role_name?: string;
  casino_pts?: number | string;
  sport_pts?: number | string;
  third_party_pts?: number | string;
  thirdPartyPts?: number | string;
  profit_loss?: number | string;
  profitAndLoss?: number | string;
}

interface BalanceReportMember {
  id: string;
  username: string;
  type: string;
  created_at: string;
  closing_balance: number;
  casino_balance: number;
  sports_balance: number;
}

const toFixedValue = (value: number | string | undefined) =>
  Number(parseNumber({ value: value ?? 0, type: "float" })).toFixed(2);

const ProfitLossReportPage = () => {
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [rows, setRows] = useState<ProfitLossRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const loggedInUserId = useSelector((state: RootState) => state.user.userData?.user_id ?? "");

  useSyncGlobalPageLoader(loading);

  const handleLoad = async () => {
    if (!loggedInUserId) return;
    setLoading(true);
    try {
      const params: Record<string, string> = {
        adminId: loggedInUserId,
        memberType: filterType,
        scope: "recursive",
      };
      const searchQuery = searchText.trim();
      if (searchQuery) {
        params.search = searchQuery;
      }

      const { data } = await instance.get("admin/downline-members-balance-report", {
        params,
      });
      const members: BalanceReportMember[] = Array.isArray(data?.data?.members)
        ? data.data.members
        : Array.isArray(data?.data?.profitLossReport)
          ? data.data.profitLossReport
          : Array.isArray(data?.data?.list)
            ? data.data.list
            : Array.isArray(data?.data)
              ? data.data
              : [];

      const list: ProfitLossRow[] = members.map((member) => ({
        user_name: member.username,
        level: member.type,
        role_name: member.type,
        casino_pts: member.casino_balance ?? 0,
        sport_pts: member.sports_balance ?? 0,
        third_party_pts: 0,
        profit_loss: member.closing_balance ?? 0,
      }));

      setRows(list);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredList = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.user_name, row.level, row.role_name].some((field) =>
        String(field ?? "").toLowerCase().includes(q)
      )
    );
  }, [rows, searchText]);

  return (
    <div className="listing-grid report-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Profit Loss</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <form method="post" onSubmit={(e) => e.preventDefault()}>
                <div className="row row5">
                  <div className="form-group col-md-3">
                    <select
                      className="custom-select"
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value as FilterType)}
                    >
                      <option value="all">All</option>
                      <option value="user">User</option>
                    </select>
                  </div>
                  <div className="form-group col-md-9">
                    <button type="button" className="btn btn-primary" onClick={handleLoad} disabled={loading}>
                      {loading ? "Loading..." : "Load"}
                    </button>
                  </div>
                </div>
              </form>

              <div className="row">
                <div className="col-sm-12 col-md-12">
                  <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Search:
                      <input
                        type="search"
                        placeholder="Search..."
                        className="form-control ml-2 form-control"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="table-responsive mb-0 report-table">
                <table
                  id="plTbl"
                  role="table"
                  aria-busy="false"
                  aria-colcount={7}
                  className="table b-table table no-footer table-hover table-striped table-bordered"
                >
                  <thead>
                    <tr role="row">
                      <th role="columnheader" scope="col" aria-colindex={1}>
                        <div>No</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={2}>
                        <div>User Name</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={3}>
                        <div>Level</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={4} className="text-right">
                        <div>Casino Pts</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={5} className="text-right">
                        <div>Sport Pts</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={6} className="text-right">
                        <div>Third Party Pts</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex={7} className="text-right">
                        <div>Profit/Loss</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.length === 0 ? (
                      <tr className="b-table-empty-row">
                        <td colSpan={7}>
                          <div role="alert" aria-live="polite">
                            <p className="text-center mb-0">
                              {loading ? "Loading..." : "No data available in table"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredList.map((row, idx) => (
                        <tr key={idx} role="row">
                          <td aria-colindex={1}>{idx + 1}</td>
                          <td aria-colindex={2}>{row.user_name ?? "-"}</td>
                          <td aria-colindex={3}>{row.level ?? row.role_name ?? "-"}</td>
                          <td aria-colindex={4} className="text-right">
                            <p className="mb-0">{toFixedValue(row.casino_pts)}</p>
                          </td>
                          <td aria-colindex={5} className="text-right">
                            <p className="mb-0">{toFixedValue(row.sport_pts)}</p>
                          </td>
                          <td aria-colindex={6} className="text-right">
                            <p className="mb-0">{toFixedValue(row.third_party_pts ?? row.thirdPartyPts)}</p>
                          </td>
                          <td aria-colindex={7} className="text-right">
                            <p className="mb-0">{toFixedValue(row.profit_loss ?? row.profitAndLoss)}</p>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr role="row">
                      <th scope="col" aria-colindex={1}>
                        <span />
                      </th>
                      <th scope="col" aria-colindex={2}>
                        <span />
                      </th>
                      <th scope="col" aria-colindex={3}>
                        <span />
                      </th>
                      <th scope="col" aria-colindex={4} className="text-right">
                        <span />
                      </th>
                      <th scope="col" aria-colindex={5} className="text-right">
                        <span />
                      </th>
                      <th scope="col" aria-colindex={6} className="text-right">
                        <span />
                      </th>
                      <th scope="col" aria-colindex={7} className="text-right">
                        <span />
                      </th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossReportPage;
